import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Search, Filter, FileText, TrendingUp, Brain } from 'lucide-react';
import { Essay, EssayType, EssayStatus } from '../types';
import { EssayCard, EssayEditor } from '../components/essays';
import { Button } from '../components/shared/Button';
import { Card } from '../components/shared/Card';

const mockEssays: Essay[] = [
  {
    id: '1',
    title: 'Why I Want to Study Computer Science',
    content: 'From a young age, I have been fascinated by the intersection of logic and creativity that defines computer science. When I first learned to code in sixth grade, I discovered that programming was not just about following instructions, but about solving problems in innovative ways...',
    type: 'academic-interest',
    status: 'revised',
    createdDate: new Date(2024, 1, 15),
    lastModified: new Date(2024, 2, 10),
    wordCount: 487,
    aiAnalysis: {
      overallScore: 87,
      strengths: ['Clear passion for the subject', 'Good personal anecdotes', 'Strong opening'],
      improvements: ['Strengthen conclusion', 'Add more specific examples'],
      structure: {
        hasHook: true,
        hasThesis: true,
        hasConclusion: true,
        paragraphFlow: 85,
        transitions: 78
      },
      language: {
        vocabularyLevel: 82,
        sentenceVariety: 79,
        grammar: 94,
        tone: 'engaging',
        readability: 85
      },
      authenticity: 91,
      engagement: 83,
      clarity: 88
    },
    feedback: [
      {
        id: 'f1',
        type: 'suggestion',
        category: 'structure',
        message: 'Consider adding a stronger conclusion that ties back to your opening.',
        position: { start: 400, end: 487 },
        severity: 'medium',
        suggestion: 'End with a compelling vision of your future contributions to the field.'
      }
    ]
  },
  {
    id: '2',
    title: 'My Leadership Journey',
    content: 'Leadership is not about being the loudest voice in the room, but about empowering others to find their own voices. This lesson became clear to me during my time as captain of the middle school debate team...',
    type: 'extracurricular',
    status: 'draft',
    createdDate: new Date(2024, 2, 5),
    lastModified: new Date(2024, 2, 12),
    wordCount: 324,
    feedback: []
  },
  {
    id: '3',
    title: 'Overcoming Math Anxiety',
    content: 'Numbers used to be my enemy. Every math test felt like a battle I was destined to lose...',
    type: 'challenge-overcome',
    status: 'in-review',
    createdDate: new Date(2024, 1, 28),
    lastModified: new Date(2024, 2, 8),
    wordCount: 256,
    feedback: []
  }
];

export const Essays: React.FC = () => {
  const [essays, setEssays] = useState<Essay[]>(mockEssays);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingEssay, setEditingEssay] = useState<Essay | undefined>();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<EssayType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<EssayStatus | 'all'>('all');

  const essayTypes: { value: EssayType | 'all'; label: string }[] = [
    { value: 'all', label: 'All Types' },
    { value: 'personal-statement', label: 'Personal Statement' },
    { value: 'why-college', label: 'Why College' },
    { value: 'extracurricular', label: 'Extracurricular' },
    { value: 'challenge-overcome', label: 'Challenge Overcome' },
    { value: 'academic-interest', label: 'Academic Interest' },
    { value: 'supplemental', label: 'Supplemental' },
    { value: 'scholarship', label: 'Scholarship' }
  ];

  const statusOptions: { value: EssayStatus | 'all'; label: string }[] = [
    { value: 'all', label: 'All Status' },
    { value: 'draft', label: 'Draft' },
    { value: 'in-review', label: 'In Review' },
    { value: 'revised', label: 'Revised' },
    { value: 'final', label: 'Final' }
  ];

  const filteredEssays = essays.filter(essay => {
    const matchesSearch = essay.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         essay.content.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || essay.type === filterType;
    const matchesStatus = filterStatus === 'all' || essay.status === filterStatus;
    return matchesSearch && matchesType && matchesStatus;
  });

  const handleSaveEssay = (essayData: Omit<Essay, 'id'>) => {
    if (editingEssay) {
      setEssays(prev => prev.map(essay =>
        essay.id === editingEssay.id
          ? { ...essayData, id: editingEssay.id }
          : essay
      ));
    } else {
      const newEssay: Essay = {
        ...essayData,
        id: Math.random().toString(36).substr(2, 9),
      };
      setEssays(prev => [newEssay, ...prev]);
    }
    setEditingEssay(undefined);
    setIsEditorOpen(false);
  };

  const handleEditEssay = (essay: Essay) => {
    setEditingEssay(essay);
    setIsEditorOpen(true);
  };

  const handleDeleteEssay = (id: string) => {
    if (window.confirm('Are you sure you want to delete this essay?')) {
      setEssays(prev => prev.filter(essay => essay.id !== id));
    }
  };

  const totalWords = essays.reduce((sum, essay) => sum + essay.wordCount, 0);
  const analyzedEssays = essays.filter(essay => essay.aiAnalysis).length;
  const avgScore = analyzedEssays > 0
    ? essays
        .filter(essay => essay.aiAnalysis)
        .reduce((sum, essay) => sum + essay.aiAnalysis!.overallScore, 0) / analyzedEssays
    : 0;

  if (isEditorOpen) {
    return (
      <EssayEditor
        essay={editingEssay}
        onSave={handleSaveEssay}
        onClose={() => {
          setIsEditorOpen(false);
          setEditingEssay(undefined);
        }}
      />
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col lg:flex-row lg:items-center lg:justify-between"
      >
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center">
            <Brain className="w-8 h-8 mr-3 text-primary-500" />
            AI Essay Editor
          </h1>
          <p className="text-lg text-gray-600">
            Write compelling essays with real-time AI feedback and analysis
          </p>
        </div>

        <Button
          onClick={() => setIsEditorOpen(true)}
          className="flex items-center space-x-2 mt-4 lg:mt-0"
        >
          <Plus size={20} />
          <span>New Essay</span>
        </Button>
      </motion.div>

      {/* Stats */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className="grid grid-cols-1 md:grid-cols-4 gap-6"
      >
        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-primary-100 rounded-xl mr-4">
              <FileText className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Essays</p>
              <p className="text-2xl font-bold text-gray-900">{essays.length}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-secondary-100 rounded-xl mr-4">
              <FileText className="w-6 h-6 text-secondary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Total Words</p>
              <p className="text-2xl font-bold text-gray-900">{totalWords.toLocaleString()}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-accent-100 rounded-xl mr-4">
              <Brain className="w-6 h-6 text-accent-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">AI Analyzed</p>
              <p className="text-2xl font-bold text-gray-900">{analyzedEssays}</p>
            </div>
          </div>
        </Card>

        <Card>
          <div className="flex items-center">
            <div className="p-3 bg-primary-100 rounded-xl mr-4">
              <TrendingUp className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Avg AI Score</p>
              <p className="text-2xl font-bold text-gray-900">
                {avgScore > 0 ? Math.round(avgScore) : '--'}
              </p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Filters */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
      >
        <Card>
          <div className="flex flex-col lg:flex-row lg:items-center space-y-4 lg:space-y-0 lg:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search essays..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <Filter size={20} className="text-gray-400" />
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value as EssayType | 'all')}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  {essayTypes.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as EssayStatus | 'all')}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                {statusOptions.map(status => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Essays Grid */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4 }}
        className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
      >
        {filteredEssays.map((essay, index) => (
          <motion.div
            key={essay.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * index }}
          >
            <EssayCard
              essay={essay}
              onEdit={handleEditEssay}
              onDelete={handleDeleteEssay}
            />
          </motion.div>
        ))}
      </motion.div>

      {filteredEssays.length === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <FileText className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm || filterType !== 'all' || filterStatus !== 'all'
              ? 'No essays found'
              : 'No essays yet'
            }
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm || filterType !== 'all' || filterStatus !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Start writing your first essay with AI-powered assistance'
            }
          </p>
          {!searchTerm && filterType === 'all' && filterStatus === 'all' && (
            <Button onClick={() => setIsEditorOpen(true)}>
              Write Your First Essay
            </Button>
          )}
        </motion.div>
      )}
    </div>
  );
};